import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import DeviceModel from '../../../../../services/deviceModel.model';
import {DeviceModelService} from '../../../../../services/deviceModel.service';
import * as _ from 'lodash';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

/**
 * DeviceModelComponent
 *
 * This component is responsible for managing Device Models within the Hardware Management module.
 * It provides a user interface for administrators to view, create, update, and search for device models.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all device models.
 * - Provide search functionality to filter device models by name.
 * - Manage the creation of new device models with validation.
 * - Handle updates to existing device models.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update device models.
 * - **Real-time Search**: Filter list by device model name.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures device model names follow specific patterns (alphanumeric, spaces, underscores, hyphens).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all device models from `DeviceModelService`.
 * 3. Formats dates using `moment`.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addUpdateDeviceModel` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-device-model
 * @example
 * <app-device-model></app-device-model>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-device-model',
  templateUrl: './device-model.component.html',
  styleUrls: ['./device-model.component.scss']
})
export class DeviceModelComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched device models from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {DeviceModel[]}
   * @default []
   */
  public deviceModels: DeviceModel[] = [];

  /**
   * Array holding the currently displayed device models.
   * This list is populated by the BatchLoader (subset of deviceModels or filteredDeviceModels).
   * @type {DeviceModel[]}
   * @default []
   */
  public deviceModelsList: DeviceModel[] = [];

  /**
   * Model for the current device model being added or edited.
   * Bound to the form for data manipulation.
   * @type {DeviceModel}
   */
  public deviceModel: DeviceModel = new DeviceModel();

  /**
   * Array holding device models that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {DeviceModel[]}
   * @default []
   */
  public filteredDeviceModels: DeviceModel[] = [];

  /**
   * Flag indicating if the page is currently loading data.
   * @type {boolean}
   * @default false
   */
  public pgLoad = false;

  /**
   * Name of the device model (used for duplicate checking).
   * @type {string}
   */
  public modelName: string;

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public deviceModelDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Reactive form group for device model details (name, abbreviation, description).
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   */
  enableDisablePageLoader: boolean;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for DeviceModelComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphanumeric, spaces, underscores, hyphens).
   * - abbreviation: Required, Max 10, Pattern (alphanumeric).
   * - description: Max 200 characters.
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {DeviceModelService} deviceModelService - Service for Device Model API operations.
   * @service DeviceModelService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public deviceModelService: DeviceModelService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.deviceModel = new DeviceModel();
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z0-9 _-]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all device models from the backend.
   * 5. Formats the 'updatedAt' date for each device model.
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.clear();
    this.search = '';
    this.deviceModels = [];
    this.deviceModelsList = [];
    this.filteredDeviceModels = [];
    this.isFiltering = false;
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.deviceModels = await this.deviceModelService.getDeviceModals();
      this.pgLoad = true;
      if (this.deviceModels.length > 0) {
        this.deviceModels.forEach(eachDeviceModel => {
          eachDeviceModel.updatedAt = JSON.parse(JSON.stringify(moment(eachDeviceModel.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredDeviceModels : this.deviceModels,
      (updatedList) => {
        this.deviceModelsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  get f() {
    return this.registerForm.controls;
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.deviceModelService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Filters the device model list based on the search query.
   *
   * - Filters by name (case-insensitive).
   * - Updates `filteredDeviceModels`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredDeviceModels`, `deviceModelsList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.deviceModels.filter(model =>
        model.name?.toLowerCase().includes(query)
      )
      : this.deviceModels;
    this.filteredDeviceModels = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.deviceModelsList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.deviceModel = new DeviceModel();
    this.deviceModelDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
    this.modelName = '';
  }

  /**
   * Handles the submission of the Device Model form (Add or Update).
   *
   * - Validates the form.
   * - Checks for duplicate device model names.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateDeviceModel
   * @returns {Promise<void | string>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateDeviceModel() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    let modelname = formData.name;
    try {
      this.deviceModelDoubleClick = true;
      this.deviceModel.name = this.modelName;
      if (this.submitButton) {
        await this.deviceModelService.create(formData);
        this.toastrService.success('Device model created successfully', '', {
          timeOut: 3000
        });
      } else {
        const updateDeviceModel =  {...formData}
        delete updateDeviceModel.id;
        await this.deviceModelService.updateOneByID(this.deviceModel.id, updateDeviceModel);
        this.toastrService.success('Device model updated successfully', '', {
          timeOut: 3000
        });
      }
      await this.ngOnInit();
      this.modelName = '';
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.deviceModelDoubleClick = false;
    }
  }

  /**
   * Retrieves a Device Model by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getDeviceModelById
   * @param {string} id - The ID of the device model to edit.
   * @returns {void}
   */
  public getDeviceModelById(id: string) {
    this.deviceModelDoubleClick = false;
    this.updateButton = true;
    this.submitButton = false;
    this.showForm = true;
    const foundDeviceModel = this.deviceModelsList.find(model => model?.id === id);
    this.deviceModel = foundDeviceModel ? { ...foundDeviceModel } : new DeviceModel(); // clone object
    this.modelName = this.deviceModel.name
    if (this.deviceModel) {
      this.registerForm.patchValue({
        name: this.deviceModel.name,
        abbreviation: this.deviceModel.abbreviation,
        description: this.deviceModel.description
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
